import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useRouter } from '@tanstack/react-router';
import { isNative } from '@/lib/platform';
import { events } from '@/lib/platform/generated/bindings.gen';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { useCalendarStore } from '@/stores/use-calendar-store';

// Allowlist of valid route prefixes — prevents XSS / path traversal via
// malicious nula:// deep links. Only paths emitted by our Rust handler
// are accepted; anything else is silently dropped.
//
// The Rust handler still emits the legacy `document/UUID` and
// `collection/UUID` shapes — we accept them here for back-compat and
// rewrite to the unified `/$workspaceId/$entityId` URL on the way through.
const ENTITY_PATH_RE = /^\/(?:document|collection)\/([0-9a-f-]{36})$/;
const STATIC_PATH_RE = /^\/(?:home|documents|collections|trash)$/;

interface ResolvedTarget {
  to: string;
}

function resolveDeepLinkTarget(path: string): ResolvedTarget | null {
  // Per-kind entity paths get rewritten to /$workspaceId/$entityId after T10.
  // Without a workspace id we can't construct the unified URL — drop the nav
  // (the previous fallback to the legacy /document/<id> path is gone now).
  const entityMatch = ENTITY_PATH_RE.exec(path);
  if (entityMatch) {
    const entityId = entityMatch[1];
    const workspaceId = useWorkspaceStore.getState().activeWorkspaceId;
    if (!workspaceId) return null;
    return { to: `/${workspaceId}/${entityId}` };
  }

  // Static workspace views stay rooted at the bare path; the workspace prefix
  // is injected by the workspace store's active selection when needed.
  if (STATIC_PATH_RE.test(path)) {
    const workspaceId = useWorkspaceStore.getState().activeWorkspaceId;
    return { to: workspaceId ? `/${workspaceId}${path}` : path };
  }

  return null;
}

export function DeepLinkBridge() {
  const router = useRouter();

  useEffect(() => {
    if (!isNative) return;

    let unlistenNavigate: (() => void) | null = null;
    let unlistenCalendar: (() => void) | null = null;

    (async () => {
      try {
        unlistenNavigate = await events.navigate.listen((event) => {
          const target = resolveDeepLinkTarget(event.payload.path);
          if (!target) {
            console.warn('[DeepLinkBridge] Blocked or unresolvable deep link path:', event.payload.path);
            return;
          }
          void router.navigate({ to: target.to as never });
        });

        unlistenCalendar = await events.calendarConnected.listen(() => {
          useCalendarStore.getState().fetchConnections();
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error('[DeepLinkBridge] Failed to setup deep link listener:', error);
      }
    })();

    return () => {
      unlistenNavigate?.();
      unlistenCalendar?.();
    };
  }, [router]);

  return null;
}
